/**
 * Codex Design System — CSS Custom Properties
 *
 * Emitted globally via MuiCssBaseline so every component — including
 * plain SCSS files — can reference tokens via var(--codex-*) without
 * importing the JS theme.
 *
 * Naming convention (Figma Foundations §12.2):
 *   --spacing-{n}        spacing scale
 *   --radius-{name}      border radius
 *   --font-{role}        font family strings
 *   --font-weight-{name} font weights
 *   --icon-{size}        icon size values
 *   --duration-{name}    motion durations
 *   --color-{role}       semantic color tokens
 *   --color-{palette}-{step} primitive color tokens (brand only)
 */

import { fontFamilies, fontWeights } from '../tokens/typography';
import { durations } from '../tokens/motion';
import { palettes } from '../tokens/palettes';
import type { SemanticColors } from '../semantic/types';

export function buildCssVariables(c: SemanticColors): string {
  return `
    :root {
      /* ── Spacing ─────────────────────────────────────── */
      --spacing-0: 0px;
      --spacing-0-5: 2px;
      --spacing-1: 4px;
      --spacing-1-5: 6px;
      --spacing-2: 8px;
      --spacing-2-5: 10px;
      --spacing-3: 12px;
      --spacing-3-5: 14px;
      --spacing-4: 16px;
      --spacing-5: 20px;
      --spacing-6: 24px;
      --spacing-8: 32px;
      --spacing-10: 40px;
      --spacing-12: 48px;
      --spacing-16: 64px;
      --spacing-18: 72px;
      --spacing-20: 80px;
      --spacing-24: 96px;

      /* ── Border Radius ───────────────────────────────── */
      --radius-none: 0px;
      --radius-sm: 4px;
      --radius-md: 8px;
      --radius-lg: 16px;
      --radius-xl: 24px;
      --radius-2xl: 32px;
      --radius-full: 9999px;

      /* ── Font Families ───────────────────────────────── */
      --font-logo: ${fontFamilies.logo};
      --font-heading: ${fontFamilies.heading};
      --font-body: ${fontFamilies.body};
      --font-caption: ${fontFamilies.caption};
      --font-mono: ${fontFamilies.mono};

      /* ── Font Weights ────────────────────────────────── */
      --font-weight-light: ${fontWeights.light};
      --font-weight-regular: ${fontWeights.regular};
      --font-weight-medium: ${fontWeights.medium};
      --font-weight-semibold: ${fontWeights.semibold};
      --font-weight-bold: ${fontWeights.bold};
      --font-weight-extrabold: ${fontWeights.extrabold};

      /* ── Icon Sizes ──────────────────────────────────── */
      --icon-xs: 12px;
      --icon-sm: 16px;
      --icon-md: 20px;
      --icon-lg: 24px;
      --icon-xl: 32px;

      /* ── Motion ──────────────────────────────────────── */
      --motion-scale: 1;
      --duration-shortest: ${durations.shortest}ms;
      --duration-shorter: ${durations.shorter}ms;
      --duration-short: ${durations.short}ms;
      --duration-entering: ${durations.entering}ms;
      --duration-leaving: ${durations.leaving}ms;
      --duration-standard: ${durations.standard}ms;
      --duration-complex: ${durations.complex}ms;

      /* ── Breakpoints (reference — use MUI breakpoints in JS) ── */
      --breakpoint-sxs: 300px;
      --breakpoint-xs: 475px;
      --breakpoint-sm: 640px;
      --breakpoint-md: 768px;
      --breakpoint-lg: 1024px;
      --breakpoint-xl: 1280px;
      --breakpoint-2xl: 1536px;

      /* ── Semantic colors ─────────────────────────────── */
      --color-bg-body: ${c.bgBody};
      --color-bg-surface: ${c.bgSurface};
      --color-bg-elevated: ${c.bgElevated};
      --color-bg-elevated-md: ${c.bgElevatedMd};
      --color-bg-elevated-lg: ${c.bgElevatedLg};
      --color-bg-elevated-xl: ${c.bgElevatedXl};
      --color-bg-hover: ${c.bgHover};

      /* Chip / status surface tints — pair each with the matching
         --color-chip-text-* token to clear AAA on the chip. */
      --color-bg-success: ${c.bgSuccess};
      --color-bg-error: ${c.bgError};
      --color-bg-warning: ${c.bgWarning};
      --color-bg-info: ${c.bgInfo};

      --color-chip-text-success: ${c.chipTextSuccess};
      --color-chip-text-error: ${c.chipTextError};
      --color-chip-text-warning: ${c.chipTextWarning};
      --color-chip-text-info: ${c.chipTextInfo};

      --color-text-primary: ${c.textPrimary};
      --color-text-secondary: ${c.textSecondary};
      --color-text-disabled: ${c.textDisabled};
      --color-text-placeholder: ${c.textPlaceholder};
      --color-text-inverse: ${c.textInverse};
      --color-text-link: ${c.textLink};
      --color-text-error: ${c.textError};
      --color-text-success: ${c.textSuccess};
      --color-text-warning: ${c.textWarning};
      --color-text-info: ${c.textInfo};
      --color-text-brand: ${c.textBrand};

      --color-border-default: ${c.borderDefault};
      --color-border-strong: ${c.borderStrong};
      --color-border-subtle: ${c.borderSubtle};
      --color-border-focus: ${c.borderFocus};

      --color-primary: ${c.primary};
      --color-secondary: ${c.secondary};
      --color-accent: ${c.accent};
      --color-info: ${c.info};
      --color-success: ${c.success};
      --color-error: ${c.error};
      --color-warning: ${c.warning};

      /* ── Brand primitive tokens (mode-agnostic) ──────── */
      --color-tyrian-500: ${palettes.tyrian[500]};
      --color-madder-500: ${palettes.madder[500]};
      --color-iris-500: ${palettes.iris[500]};
      --color-lapis-500: ${palettes.lapis[500]};
      --color-laurel-500: ${palettes.laurel[500]};
      --color-cinnabar-500: ${palettes.cinnabar[500]};
      --color-saffron-500: ${palettes.saffron[500]};
    }

    /* Reduced motion — collapses all transition durations */
    @media (prefers-reduced-motion: reduce) {
      :root { --motion-scale: 0; }
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
}
